import { jsx } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { Table } from "@zillow/constellation-10";
import makeContentInteractive from "../../util/makeSpansInteractive.esm.js";
const APPEARANCE_ALIASES = {
  barebones: "bare"
};
const APPEARANCES = ["bare", "horizontal", "grid", "zebra"];
const SIZES = ["sm", "md", "lg"];
const isOneOf = (values, value) => values.includes(value);
const resolveAppearance = (appearance) => {
  const mapped = APPEARANCE_ALIASES[appearance ?? ""] ?? appearance;
  return isOneOf(APPEARANCES, mapped) ? mapped : "grid";
};
const resolveSize = (size) => isOneOf(SIZES, size) ? size : "md";
const resolveStickyHeader = ({
  stickyHeader,
  head
}) => !!stickyHeader && !!(head == null ? void 0 : head.length);
const resolveResponsiveStyles = (sticky) => sticky ? css.raw({
  maxHeight: "80vh"
}) : void 0;
const resolveAlignments = ({
  body,
  head
}) => {
  var _a, _b;
  const template = ((_a = body == null ? void 0 : body[0]) == null ? void 0 : _a.cells) ?? ((_b = head == null ? void 0 : head[0]) == null ? void 0 : _b.cells);
  if (!template) {
    return void 0;
  }
  return template.map((cell) => (cell == null ? void 0 : cell.align) || "left");
};
const TSection = ({
  name,
  sticky,
  children
}) => (
  // `sticky` is only valid on Table.Header; Table.Body would spread it onto
  // <tbody>. It only takes effect because Table.Responsive is height-capped —
  // see resolveResponsiveStyles.
  name === "head" ? /* @__PURE__ */ jsx(Table.Header, { sticky, children }) : /* @__PURE__ */ jsx(Table.Body, { children })
);
const TCell = ({
  name,
  children
}) => name === "head" ? /* @__PURE__ */ jsx(Table.HeaderCell, { children }) : /* @__PURE__ */ jsx(Table.Cell, { children });
const SECTIONS = ["head", "body"];
const BlockTable = ({
  attrs
}) => {
  const {
    head,
    body,
    appearance,
    size,
    stickyHeader,
    ariaLabel,
    ...otherProps
  } = attrs;
  const sticky = resolveStickyHeader({
    stickyHeader,
    head
  });
  const rowsBySection = {
    head,
    body
  };
  return /* @__PURE__ */ jsx(Table.Responsive, { css: resolveResponsiveStyles(sticky), children: /* @__PURE__ */ jsx(
    Table.Root,
    {
      alignments: resolveAlignments(attrs),
      appearance: resolveAppearance(appearance),
      "aria-label": ariaLabel || void 0,
      size: resolveSize(size),
      ...otherProps,
      children: SECTIONS.map((name) => {
        var _a;
        return /* @__PURE__ */ jsx(TSection, { name, sticky, children: (_a = rowsBySection[name]) == null ? void 0 : _a.map(({
          cells
        }, rowIndex) => /* @__PURE__ */ jsx(Table.Row, { children: cells.map(({
          content
        }, columnIndex) => /* @__PURE__ */ jsx(TCell, { name, children: makeContentInteractive(content) }, columnIndex)) }, rowIndex)) }, name);
      })
    }
  ) });
};
export {
  BlockTable as default,
  resolveAlignments,
  resolveAppearance,
  resolveResponsiveStyles,
  resolveSize,
  resolveStickyHeader
};
